import Box from "@/main/components/shared/box";
import Button from "@/main/components/shared/button/button";
import { getDefaultCountryOptionDrop, getTranslateByKey } from "@/main/services/utils";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as S from "./styles";

import useFormQueryParams from "@/main/hooks/useformQueryParams";

import { getBodyshops, resetReducerBodyshops, useBodyshopReducer } from "@/main/store/bodyshop";
import { getInsurers, resetReducerInsurers, useInsurerReducer } from "@/main/store/insurer";
import { getStatesUser, useStateReducer } from "@/main/store/state";
import { getSuppliers, resetReducerSuppliers, useSupplierReducer } from "@/main/store/supplier";

import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import KogKeyboardDatePickerContainer from "@/main/components/shared/datepicker/keyboardDatePickerContainer";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import VehicleAutoCompleteComponent from "@/main/components/shared/vehicle/VehicleAutoCompleteComponent";

import { VehicleAutoCompleteTypeEnum } from "@/main/services/enum/VehicleAutoCompleteTypeEnum";
import { isNullUndefined } from "@/main/services/reducerUtils";
import { userIsInsurer } from "@/main/services/utilsUser";

import {
	ISearchCancelledPartsForm,
	getInitialSearchCancelledPartsForm,
	searchCancelledPartsSchema,
} from "../../cancelledPartsScreenUtils";

import KogInputAutoCompleteCheckControlled from "@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled";
import { KeyValue } from "@/main/models/common";
import {
	BodyShopFilterRequest,
	InsurerFilterRequest,
	StateFilterRequest,
	SupplierFilterRequest,
} from "@/main/models/request";
import { HeadQuarterBranchFilterEnum } from "@/main/services/enum/HeadQuarterBranchFilterEnum";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { validateFormDate } from "@/main/services/utilsDate";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { zodResolver } from "@hookform/resolvers/zod";

const ID_BASE_COMPONENTS = "id-searchPartsCancelled";

type IFiltersCancelledParts = {
	onSubmit: (data: ISearchCancelledPartsForm) => void;
	onClear: () => void;
};

export default function FiltersCancelledParts({ onSubmit, onClear }: Readonly<IFiltersCancelledParts>) {
	const dispatch = useDispatch();

	const maxRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.SearchRangeDaysManagePartsCancelled);

	const [hasClear, setHasClear] = useState(false);
	const [showFilters, setShowFilters] = useState(true);

	const { insurers: insurerReducer } = useInsurerReducer();
	const { data: dataInsurers, isLoading: isLoadingInsurers } = insurerReducer;

	const { suppliers: supplierReducer } = useSupplierReducer();
	const { data: dataSupplier, isLoading: isLoadingSupplier } = supplierReducer;

	const { bodyshops: bodyshopReducer } = useBodyshopReducer();

	const { statesUser: stateReducer } = useStateReducer();
	const { data: dataStates, isLoading: isLoadingStates } = stateReducer;

	const { countriesUser: countryReducer } = useCountryReducer();

	const optionDefaultInsurerDrop = dataInsurers !== null && dataInsurers.length === 1 ? dataInsurers[0] : null;

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.data);

	const {
		control,
		setValue,
		customHandleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useFormQueryParams<ISearchCancelledPartsForm>({
		mode: "onChange",
		defaultValues: getInitialSearchCancelledPartsForm(maxRangeDays),
		onSubmit: handleSubmit,
		resolver: zodResolver(searchCancelledPartsSchema),
		mapDefaultValues: {
			insurer: insurerReducer,
			deliveryAddressState: stateReducer,
			bodyshop: bodyshopReducer,
			supplier: supplierReducer,
		} as any,
	});

	function handleSubmit(formData: ISearchCancelledPartsForm) {
		validateFormDate(formData.startDate, formData.finalDate, maxRangeDays);
		setShowFilters(false);
		onSubmit(formData);
	}

	function handleClear() {
		const defaultForm = getInitialSearchCancelledPartsForm(maxRangeDays);
		if (userIsInsurer()) {
			reset({
				...defaultForm,
				insurer: optionDefaultInsurerDrop,
			});
		} else {
			reset(defaultForm);
		}
		onClear();
	}

	function handleFilters() {
		setShowFilters(!showFilters);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function callInsurerApiDomain(bodyshopSelected?: KeyValue | KeyValue[] | null) {
		if (isNullUndefined(optionDefaultCountryDrop)) return;

		dispatch(resetReducerInsurers());
		const request: InsurerFilterRequest = {
			type: HeadQuarterBranchFilterEnum.All,
			idStatusRegister: StatusRegisterEnum.Active,
		};

		if (bodyshopSelected) {
			const bodyshops = Array.isArray(bodyshopSelected) ? bodyshopSelected : [bodyshopSelected];

			request.guidsBodyshop = bodyshops.map((x) => String(x.key));
		}

		dispatch(getInsurers(request));
	}

	function callBodyshopApiDomain(insurerSelected: KeyValue | null = null) {
		if (isNullUndefined(optionDefaultCountryDrop)) return;

		dispatch(resetReducerBodyshops());
		const request: BodyShopFilterRequest = {
			...(insurerSelected && { guidInsurer: String(insurerSelected.key) }),
			type: HeadQuarterBranchFilterEnum.All,
			idStatusRegister: StatusRegisterEnum.Active,
		};
		dispatch(getBodyshops(request));
	}

	function callSupplierApiDomain() {
		// Clean fields & reducers
		dispatch(resetReducerSuppliers());

		if (optionDefaultCountryDrop !== null) {
			const request: SupplierFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop?.key),
				type: HeadQuarterBranchFilterEnum.All,
				idStatusRegister: StatusRegisterEnum.Active,
			};
			dispatch(getSuppliers(request));
		}
	}

	useEffect(() => {
		callSupplierApiDomain();
		callInsurerApiDomain();
		callBodyshopApiDomain();
		if (optionDefaultCountryDrop !== null) {
			const requestState: StateFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop?.key),
			};
			dispatch(getStatesUser(requestState));
		}
	}, [optionDefaultCountryDrop?.key]);

	useEffect(() => {
		if (isLoadingInsurers || !dataInsurers) return;

		if (userIsInsurer()) {
			setValue("insurer", optionDefaultInsurerDrop);

			callBodyshopApiDomain(optionDefaultInsurerDrop);
			callSupplierApiDomain();
		}
	}, [isLoadingInsurers, dataInsurers]);

	useEffect(() => {
		dispatch(getCountriesUser());
		return () => {
			dispatch(resetReducerBodyshops());
			dispatch(resetReducerInsurers());
			dispatch(resetReducerSuppliers());
		};
	}, []);

	const labelQuoteFilterVehicle = "{0} / {1} / {2} / {3}"
		.replace("{0}", getTranslateByKey("information.claimNr"))
		.replace("{1}", getTranslateByKey("information.orderNr"))
		.replace("{2}", getTranslateByKey("information.licensePlate"))
		.replace("{3}", getTranslateByKey("information.quoteNr"));

	return (
		<>
			<S.AreaButton>
				<Button id={`${ID_BASE_COMPONENTS}-btn-filters`} onClick={handleFilters}>
					{getTranslateByKey("information.filters")}
				</Button>
			</S.AreaButton>

			<Box showBox={showFilters} title={getTranslateByKey("information.filters")}>
				<form onSubmit={customHandleSubmit} noValidate>
					<S.RowInputs>
						<KogInputTextControlledNew
							id={`${ID_BASE_COMPONENTS}-input-claimNumber`}
							label={labelQuoteFilterVehicle}
							name="claimNumber"
							control={control}
						/>

						<KogKeyboardDatePickerContainer
							idScreen={`${ID_BASE_COMPONENTS}-datePicker-startDate-finalDate`}
							nameStartDate="startDate"
							nameFinalDate="finalDate"
							control={control}
							parameterRangeDays={maxRangeDays}
						/>
						<VehicleAutoCompleteComponent
							hasClearValue={hasClear}
							setHasClearValue={setHasClear}
							handleChangeVehicle={(vehicle) => {
								setValue("vehicleManufacturer", vehicle?.guidVehicleManufacturer || "");
								setValue("vehicleModel", vehicle?.guidVehicleModel || "");
								setValue("subModel", vehicle?.guidSubModel || "");
								setValue("vehicle", vehicle?.value || "");
							}}
							vehicleAutoCompleteType={VehicleAutoCompleteTypeEnum.Partial}
							vehicleValueProps={watch("vehicle")}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-insurer`}
							label={getTranslateByKey("information.insurer")}
							control={control}
							nameCustom="insurer"
							rulesCustom={{ required: true }}
							hasErrorCustom={!!errors.insurer}
							options={dataInsurers}
							isLoading={isLoadingInsurers}
							disabled={dataInsurers?.length === 1}
							setValueCustom={setValue}
							onChangeCustom={(insurerSelected) => {
								callBodyshopApiDomain(insurerSelected);
							}}
						/>

						<KogInputAutoCompleteCheckControlled
							name="bodyshop"
							disableVirtualization
							control={control}
							label={getTranslateByKey("information.bodyshop")}
							options={bodyshopReducer.data || []}
							customInputProps={{
								id: `${ID_BASE_COMPONENTS}-select-bodyshop`,
							}}
							selectAllLabel={getTranslateByKey("information.selectAll")}
							isLoading={bodyshopReducer.isLoading}
							isDefaultValueUnique={bodyshopReducer.data === null || bodyshopReducer.data.length === 1}
							onChangeCustom={(value, optionChanged) => {
								if (optionChanged?.key === "select-all") {
									return;
								}

								if (!userIsInsurer()) {
									callInsurerApiDomain(value);
								}
							}}
						/>

						<KogAutoCompleteControlled
							id={`${ID_BASE_COMPONENTS}-select-supplier`}
							label={getTranslateByKey("information.supplier")}
							control={control}
							nameCustom="supplier"
							options={dataSupplier}
							isLoading={isLoadingSupplier}
							setValueCustom={setValue}
							onChangeCustom={() => {}}
						/>

						<KogInputAutoCompleteCheckControlled
							name="deliveryAddressState"
							control={control}
							label={getTranslateByKey("information.deliveryAddressState")}
							options={dataStates ?? []}
							customInputProps={{
								id: `${ID_BASE_COMPONENTS}-select-deliveryAddressState`,
							}}
							selectAllLabel={getTranslateByKey("information.selectAll")}
							isLoading={isLoadingStates}
						/>
					</S.RowInputs>

					<S.AreaButtonFilters>
						<SecondaryButton id={`${ID_BASE_COMPONENTS}-btn-search`} type="submit">
							{getTranslateByKey("information.search")}
						</SecondaryButton>

						<SecondaryButton
							id={`${ID_BASE_COMPONENTS}-btn-clear`}
							onClick={() => {
								handleClear();
							}}
						>
							{getTranslateByKey("information.clear")}
						</SecondaryButton>
					</S.AreaButtonFilters>
				</form>
			</Box>
		</>
	);
}
